import { css } from 'aphrodite';
import * as React from 'react';
import {
	ISupportMailbox,
	useDeleteSupportMailboxMutation,
	useSupportMailboxListQuery,
} from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import { ConditionalAccess } from '../../components/ConditionalAccess';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { MailboxCreateForm } from './MailboxCreateForm';
import { PlusIcon } from './SettingsIcons';
import { mailboxTabStyles as styles } from './styles/mailboxTabStyles';
import { settingsStyles as st } from './styles/settingsStyles';

interface IMailboxTabProps {
	/** Open the given mailbox's settings (master→detail navigation). */
	onSelectMailbox: (mailboxId: string) => void;
}

/**
 * Mailbox list (settings landing). Lets the caller:
 *  - open a mailbox's settings (`onSelectMailbox` → master→detail route),
 *  - create a new mailbox (`MailboxCreateForm`) — creating one provisions live inbound email
 *    routing, so it is gated to the `OnlyForDevelopers` admin permission,
 *  - deactivate (soft-delete) a mailbox behind a branded confirm dialog.
 */
export function MailboxTab({ onSelectMailbox }: IMailboxTabProps) {
	const { data: mailboxes, isLoading, isError, error } = useSupportMailboxListQuery();
	const deleteMutation = useDeleteSupportMailboxMutation();

	const [isCreating, setIsCreating] = React.useState(false);
	const [pendingDeactivate, setPendingDeactivate] = React.useState<ISupportMailbox | null>(null);

	if (isLoading) return <LoadingSpinner />;
	if (isError) {
		return <div className={css(st.error)}>{getApiErrorMessage(error, 'Failed to load mailboxes.')}</div>;
	}

	const list = mailboxes ?? [];

	const confirmDeactivate = () => {
		if (!pendingDeactivate) return;
		deleteMutation.mutate(pendingDeactivate.id, {
			onSuccess: () => setPendingDeactivate(null),
		});
	};

	return (
		<div>
			<div className={css(styles.header)}>
				<h1 className={css(styles.title)}>Mailboxes</h1>
				<ConditionalAccess permission={AdminPermission.OnlyForDevelopers}>
					<Button onClick={() => setIsCreating(true)} disabled={isCreating} styles={[styles.newButton]}>
						<PlusIcon />
						New mailbox
					</Button>
				</ConditionalAccess>
			</div>
			<div className={css(styles.lead)}>
				Mailboxes you're an agent on. Each mailbox has its own team, categories, and saved replies.
			</div>

			{isCreating && (
				<MailboxCreateForm
					onCancel={() => setIsCreating(false)}
					onCreated={created => {
						setIsCreating(false);
						onSelectMailbox(created.id);
					}}
				/>
			)}

			{deleteMutation.isError && (
				<div className={css(st.errorMsg, styles.errorMsg)}>
					{getApiErrorMessage(deleteMutation.error, 'Failed to deactivate mailbox.')}
				</div>
			)}

			{list.length === 0 ? (
				<div className={css(styles.emptyState)}>
					You are not an agent on any mailboxes yet.{' '}
					<ConditionalAccess
						permission={AdminPermission.OnlyForDevelopers}
						elseChildren='Ask an administrator to add you.'
					>
						Create one or ask an administrator to add you.
					</ConditionalAccess>
				</div>
			) : (
				<div className={css(styles.grid)}>
					{list.map(mailbox => (
						<div key={mailbox.id} className={css(styles.card, !mailbox.isActive && styles.cardInactive)}>
							<div className={css(styles.cardTop)}>
								<div className={css(styles.cardInfo)}>
									<div className={css(styles.name)}>{mailbox.name}</div>
									<div className={css(styles.emailAddress)}>{mailbox.emailAddress}</div>
								</div>
								{mailbox.isActive ? (
									<span className={css(styles.statusPill, styles.statusActive)}>
										<span className={css(styles.statusDot)} />
										Active
									</span>
								) : (
									<span className={css(styles.statusPill, styles.statusInactive)}>Inactive</span>
								)}
							</div>
							<div className={css(styles.cardFooter)}>
								<Button variant='outline' size='sm' onClick={() => onSelectMailbox(mailbox.id)}>
									Settings
								</Button>
								{mailbox.isActive && (
									<Button
										variant='ghost'
										size='sm'
										onClick={() => setPendingDeactivate(mailbox)}
										disabled={deleteMutation.isPending}
										styles={[styles.deactivateButton]}
									>
										Deactivate
									</Button>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{pendingDeactivate && (
				<ConfirmModal
					title='Deactivate Mailbox'
					message={`Deactivate "${pendingDeactivate.name}"? It will stop receiving and routing tickets. You can reactivate it later from its settings.`}
					confirmLabel={deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
					variant='danger'
					onConfirm={confirmDeactivate}
					onCancel={() => setPendingDeactivate(null)}
				/>
			)}
		</div>
	);
}
